import type { ArticleContent } from "@/mocks/articles-shared";
import {
  IMG_GUIDE,
  IMG_REVIEW,
  IMG_SHIPPING,
  IMG_COUPON,
  IMG_DISCOUNT,
  IMG_WAREHOUSE,
  IMG_LEGIT,
  IMG_SAFETY,
  IMG_QC,
  IMG_SELLERS,
  IMG_PRODUCTS,
} from "@/mocks/articles-shared";

export const articlesEs: ArticleContent[] = [
  {
    slug: "usfans-spreadsheet-guide",
    title: "USFans Spreadsheet: la guía completa para principiantes",
    shortAnswer:
      "USFans Spreadsheet es un catálogo de productos organizado que agrupa zapatillas, sudaderas, camisetas, camisetas deportivas, accesorios y más en un solo lugar, para comparar productos, consultar notas de vendedores y entender el envío antes de comprar.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "8 min de lectura",
    heroImage: IMG_GUIDE,
    toc: ["¿Qué es USFans Spreadsheet?", "Cómo funciona el catálogo", "Categorías de productos", "Cómo encontrar productos", "Control de calidad (QC)", "Envío, almacén y costes", "¿Es legítimo y seguro?"],
    sections: [
      { heading: "¿Qué es USFans Spreadsheet?", paragraphs: ["USFans Spreadsheet es un catálogo estructurado que reúne ropa urbana y productos para fans en una lista única y fácil de leer. En lugar de hacer clic en decenas de páginas, te desplazas por una hoja organizada y ves los productos agrupados por tipo."] },
      { heading: "Cómo funciona el catálogo", paragraphs: ["Cada sección se centra en un tipo de producto, con el nombre, una breve descripción y un enlace al producto en el sitio principal. Este diseño predecible ayuda a los visitantes recurrentes a encontrar rápido lo que buscan."] },
      { heading: "Categorías de productos", paragraphs: ["El catálogo cubre zapatillas, sudaderas, camisetas, chaquetas, pantalones, gorras, conjuntos, ropa interior, camisetas deportivas, accesorios, bolsos, cinturones, perfumes y una sección general. Una estructura clara evita buscar en toda la tienda."] },
      { heading: "Cómo encontrar productos", paragraphs: ["Abre el desplegable de categorías o pulsa un botón de categoría y revisa los artículos. Cada entrada enlaza con el sitio principal, donde ves todos los detalles y precios."] },
      { heading: "Control de calidad (QC)", paragraphs: ["Busca fotos claras, notas de tallas coherentes y descripciones que mencionen material y ajuste. Los artículos con fichas detalladas son más fáciles de evaluar."] },
      { heading: "Envío, almacén y costes", paragraphs: ["El envío depende del origen y de tu ubicación. Muchos catálogos trabajan con almacenes, lo que afecta al tiempo y al coste. Revisa siempre los detalles de envío en el sitio principal antes de pedir."] },
      { heading: "¿Es legítimo y seguro?", paragraphs: ["La hoja es un catálogo informativo que enlaza con la tienda principal donde se compra. Verifica la tienda, revisa las opciones de pago y evita sitios que imitan al original."] },
    ],
    tableTitle: "Hoja de cálculo vs búsqueda tradicional",
    table: {
      headers: ["Factor", "USFans Spreadsheet", "Búsqueda tradicional"],
      rows: [
        ["Descubrir productos", "Organizado", "Disperso"],
        ["Comparación lado a lado", "Fácil", "Difícil"],
        ["Apto para principiantes", "Alto", "Medio"],
        ["Encontrar novedades", "Rápido", "Más lento"],
      ],
    },
    faq: [
      { q: "¿Es gratis explorar USFans Spreadsheet?", a: "Sí, explorar las categorías es sencillo. Los precios y el pago se gestionan en el sitio principal." },
      { q: "¿Necesito una cuenta para ver productos?", a: "Normalmente puedes explorar sin cuenta. Crearla, si es necesario, se hace en el sitio principal." },
      { q: "¿Con qué frecuencia se actualiza la hoja?", a: "La frecuencia varía. Para ofertas y precios actuales, consulta siempre el sitio principal." },
    ],
  },
  {
    slug: "usfans-review",
    title: "Reseña de USFans: qué dicen los compradores reales",
    shortAnswer:
      "Las reseñas de USFans se centran en la calidad del producto, la fiabilidad del vendedor, la velocidad de envío y la atención al cliente. Leerlas en estas áreas te ayuda a juzgar si una oferta vale tu dinero.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "6 min de lectura",
    heroImage: IMG_REVIEW,
    toc: ["Por qué importan las reseñas", "Qué suelen elogiar", "Quejas habituales", "Cómo leer una reseña"],
    sections: [
      { heading: "Por qué importan las reseñas", paragraphs: ["Las reseñas son una de las formas más rápidas de evaluar un producto y un vendedor antes de comprar. Revelan tallas reales, calidad del material y cuánto se parece el artículo a sus fotos."] },
      { heading: "Qué suelen elogiar", paragraphs: ["Las reseñas positivas suelen mencionar fotos precisas, costuras sólidas y artículos que llegan como se describen. El envío rápido y la buena comunicación también aparecen a menudo."] },
      { heading: "Quejas habituales", paragraphs: ["Las reseñas negativas suelen señalar tallas que quedan grandes o pequeñas, envío más lento o pequeñas diferencias de color. Rara vez son decisivas, pero ayudan a fijar expectativas."] },
      { heading: "Cómo leer una reseña", paragraphs: ["Busca patrones en muchas reseñas en lugar de depender de una sola opinión. Revisa primero las recientes, ya que los lotes y vendedores cambian con el tiempo."] },
    ],
    tableTitle: "Qué revelan las reseñas",
    table: {
      headers: ["Factor", "Qué buscar"],
      rows: [
        ["Fotos del producto", "Coinciden con el artículo"],
        ["Tallas", "Opiniones coherentes"],
        ["Envío", "Claro y realista"],
      ],
    },
    faq: [
      { q: "¿Son fiables las reseñas de USFans?", a: "Son una señal útil cuando se leen en conjunto. Céntrate en los patrones de muchas reseñas." },
      { q: "¿Dónde leo las reseñas de USFans?", a: "Suelen estar en las páginas de producto del sitio principal y en las discusiones de la comunidad." },
      { q: "¿Qué ignorar en las reseñas?", a: "Ignora opiniones emotivas sin detalle. Busca comentarios concretos sobre ajuste y calidad." },
    ],
  },
  {
    slug: "usfans-shipping-cost",
    title: "Coste de envío de USFans: guía completa",
    shortAnswer:
      "El coste de envío de USFans depende del peso, el destino y el método elegido. Los pedidos internacionales pueden incluir tasas de aduana que se confirman al pagar.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "6 min de lectura",
    heroImage: IMG_SHIPPING,
    toc: ["Qué afecta al coste", "Nacional vs internacional", "Almacén y plazos", "Cómo reducir costes"],
    sections: [
      { heading: "Qué afecta al coste", paragraphs: ["El coste depende del peso, el tamaño, el destino y el método elegido. Los pedidos más pesados o voluminosos suelen costar más."] },
      { heading: "Nacional vs internacional", paragraphs: ["El envío nacional suele ser más barato y rápido. El internacional añade distancia, posibles aduanas y gastos de gestión según el país."] },
      { heading: "Almacén y plazos", paragraphs: ["Los artículos desde un almacén pueden llegar antes que los enviados por separado. El plazo sigue dependiendo de tu ubicación y del transportista."] },
      { heading: "Cómo reducir costes", paragraphs: ["Combina varios artículos en un pedido cuando sea posible y compara métodos al pagar. El coste exacto se confirma siempre en el sitio principal."] },
    ],
    tableTitle: "Factores de envío",
    table: {
      headers: ["Factor", "Impacto en el coste"],
      rows: [
        ["Peso y tamaño", "Mayor para artículos grandes"],
        ["Destino", "Mayor para internacional"],
        ["Método", "Los rápidos cuestan más"],
      ],
    },
    faq: [
      { q: "¿Cuánto cuesta el envío de USFans?", a: "No hay un precio único. Depende del peso, el destino y el método, y se confirma al pagar." },
      { q: "¿USFans envía internacionalmente?", a: "Depende de las opciones de la tienda. Revisa los detalles de envío en el sitio principal para tu país." },
      { q: "¿Las aduanas están incluidas?", a: "Las tasas de aduana pueden aplicar a pedidos internacionales y suelen ser aparte del coste de envío." },
    ],
  },
  {
    slug: "usfans-coupon",
    title: "Cupón de USFans: cómo encontrar ahorros reales",
    shortAnswer:
      "Los cupones y descuentos de USFans pueden reducir el total, pero no siempre están disponibles. La forma más segura de encontrar ahorros válidos es a través de las promociones y el boletín del sitio principal.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "5 min de lectura",
    heroImage: IMG_COUPON,
    toc: ["De dónde vienen los cupones", "Cómo aplicar un cupón", "Evitar códigos falsos", "Cuándo vale la pena"],
    sections: [
      { heading: "De dónde vienen los cupones", paragraphs: ["Los cupones suelen venir de promociones de la tienda, rebajas de temporada o su boletín. Los sitios de códigos de terceros son menos fiables."] },
      { heading: "Cómo aplicar un cupón", paragraphs: ["Añade artículos al carrito e introduce el código en el campo de cupón al pagar. El descuento se aplica antes de completar el pago."] },
      { heading: "Evitar códigos falsos", paragraphs: ["Ten cuidado con códigos de sitios desconocidos o redes sociales. Usa la tienda oficial y no compartas datos de pago para obtener un descuento."] },
      { heading: "Cuándo vale la pena", paragraphs: ["Un cupón vale más en pedidos grandes. En pedidos pequeños, buscar un código puede no compensar lo ahorrado."] },
    ],
    tableTitle: "Fuentes de cupones",
    table: {
      headers: ["Fuente", "Fiabilidad"],
      rows: [
        ["Tienda oficial", "Alta"],
        ["Boletín", "Alta"],
        ["Sitios de códigos", "Baja"],
      ],
    },
    faq: [
      { q: "¿Hay cupones de USFans válidos?", a: "Puede haberlos durante promociones. Revisa las ofertas del sitio principal para códigos vigentes." },
      { q: "¿Cómo uso un código de descuento?", a: "Introdúcelo en el campo de cupón al pagar antes de completar el pedido." },
      { q: "¿Por qué no funciona mi código?", a: "Puede estar caducado, exigir un gasto mínimo o no aplicar a los artículos seleccionados." },
    ],
  },
  {
    slug: "usfans-discount-code",
    title: "Código de descuento de USFans: qué funciona y qué evitar",
    shortAnswer:
      "Los códigos de descuento de USFans pueden reducir el total cuando son válidos, pero muchos códigos en línea están caducados o son falsos. Verifica siempre el código en el sitio principal.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "5 min de lectura",
    heroImage: IMG_DISCOUNT,
    toc: ["Cómo funcionan los códigos", "Por qué fallan", "Detectar una estafa", "Ahorrar sin código"],
    sections: [
      { heading: "Cómo funcionan los códigos", paragraphs: ["Un código de descuento es un texto corto que introduces al pagar. Puede dar un porcentaje, una cantidad fija o envío gratis."] },
      { heading: "Por qué fallan", paragraphs: ["Los códigos caducan, exigen un pedido mínimo o solo funcionan en ciertos artículos. Si falla, suele ser porque ya no aplica a tu carrito."] },
      { heading: "Detectar una estafa", paragraphs: ["Evita códigos que pidan hacer clic en enlaces sospechosos o introducir datos personales. Los descuentos legítimos se aplican solo en el pago oficial."] },
      { heading: "Ahorrar sin código", paragraphs: ["Rebajas, paquetes y umbrales de envío gratis ahorran sin código. Compara el total final en lugar de perseguir un solo descuento."] },
    ],
    tableTitle: "Señales de alerta",
    table: {
      headers: ["Señal", "Significado"],
      rows: [
        ["Pide datos personales", "Probable estafa"],
        ["Promete descuentos enormes", "Demasiado bueno para ser cierto"],
        ["Campo de pago oficial", "Lugar legítimo"],
      ],
    },
    faq: [
      { q: "¿Dónde introduzco el código de USFans?", a: "Introdúcelo en el campo de promoción del pago del sitio principal." },
      { q: "¿Los códigos están garantizados?", a: "No. Pueden caducar o tener condiciones, así que verifícalos al pagar." },
      { q: "¿Puedo combinar códigos?", a: "Normalmente solo se aplica un código por pedido. Consulta los términos de la tienda." },
    ],
  },
  {
    slug: "usfans-warehouse",
    title: "Almacén de USFans: cómo afecta a tu pedido",
    shortAnswer:
      "Un almacén de USFans puede acelerar el procesamiento y hacer el stock más consistente, pero también afecta a las opciones de envío y al origen del pedido.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "6 min de lectura",
    heroImage: IMG_WAREHOUSE,
    toc: ["Qué hace un almacén", "Cómo afecta al envío", "Almacén vs vendedores", "Qué revisar antes de pedir"],
    sections: [
      { heading: "Qué hace un almacén", paragraphs: ["Un almacén guarda productos a granel para que los pedidos se preparen y envíen más rápido. Esto suele significar procesamiento más ágil y stock más estable."] },
      { heading: "Cómo afecta al envío", paragraphs: ["Los artículos desde un almacén pueden tener otros plazos y costes que los enviados por un vendedor. El origen también afecta a las aduanas."] },
      { heading: "Almacén vs vendedores", paragraphs: ["El stock de almacén suele estar estandarizado, mientras que los vendedores individuales ofrecen más variedad. Cada uno tiene distintas ventajas."] },
      { heading: "Qué revisar antes de pedir", paragraphs: ["Confirma el origen del artículo y el plazo estimado. Revisa los detalles de envío en el sitio principal para evitar sorpresas."] },
    ],
    tableTitle: "Almacén vs envío directo",
    table: {
      headers: ["Factor", "Almacén", "Vendedor"],
      rows: [
        ["Velocidad", "Suele ser mayor", "Varía"],
        ["Consistencia", "Mayor", "Varía"],
        ["Origen", "Fijo", "Varía"],
      ],
    },
    faq: [
      { q: "¿USFans usa un almacén?", a: "Muchos catálogos trabajan con almacenes. Revisa los detalles de envío en el sitio principal." },
      { q: "¿El envío desde almacén es más rápido?", a: "Suele ser más rápido y consistente, pero el plazo depende de tu ubicación." },
      { q: "¿Puedo elegir el origen?", a: "El origen lo determina la tienda, no el comprador. Revisa las opciones al pagar." },
    ],
  },
  {
    slug: "usfans-legit",
    title: "¿Es legítimo USFans? Guía de seguridad",
    shortAnswer:
      "USFans puede ser un lugar legítimo para comprar cuando usas el sitio oficial, pero debes verificar la tienda, los métodos de pago y las políticas, y evitar sitios falsos.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "6 min de lectura",
    heroImage: IMG_LEGIT,
    toc: ["Cómo verificar la tienda", "Señales de alerta", "Seguridad en el pago", "Qué es este sitio"],
    sections: [
      { heading: "Cómo verificar la tienda", paragraphs: ["Comprueba que estás en el dominio oficial y busca páginas claras de contacto y políticas. Una tienda legítima ofrece información transparente de envío y devoluciones."] },
      { heading: "Señales de alerta", paragraphs: ["Desconfía de dominios similares, peticiones de pago inusuales o precios demasiado buenos. Son signos habituales de sitios no oficiales."] },
      { heading: "Seguridad en el pago", paragraphs: ["Usa métodos seguros y nunca compartas datos de pago fuera del pago oficial. Evita transferencias a terceros desconocidos."] },
      { heading: "Qué es este sitio", paragraphs: ["Este sitio es un recurso informativo independiente, no la tienda. Verifica siempre los detalles importantes en el sitio principal antes de comprar."] },
    ],
    tableTitle: "Lista de verificación",
    table: {
      headers: ["Comprobación", "Qué hacer"],
      rows: [
        ["Dominio oficial", "Verifica la URL"],
        ["Políticas claras", "Lee envío y devoluciones"],
        ["Pago seguro", "Usa métodos de confianza"],
      ],
    },
    faq: [
      { q: "¿Es legítimo USFans?", a: "Puede serlo cuando compras por el sitio oficial. Verifica siempre la tienda y los métodos de pago." },
      { q: "¿Cómo evito sitios falsos?", a: "Usa solo el dominio oficial y desconfía de URLs similares y peticiones de pago inusuales." },
      { q: "¿Este sitio es la tienda?", a: "No. Es un recurso informativo independiente, no la tienda oficial." },
    ],
  },
  {
    slug: "usfans-safety",
    title: "Seguridad de USFans: consejos para comprar sin riesgo",
    shortAnswer:
      "Comprar con seguridad en USFans significa usar el sitio oficial, métodos de pago seguros y revisar los detalles del vendedor y del producto antes de pedir.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "6 min de lectura",
    heroImage: IMG_SAFETY,
    toc: ["Usa el sitio oficial", "Protege tu pago", "Protege tus datos", "Conoce los riesgos de envío"],
    sections: [
      { heading: "Usa el sitio oficial", paragraphs: ["Compra siempre por el sitio oficial, no por enlaces de mensajes o anuncios desconocidos. Los sitios falsos son una forma habitual de engaño."] },
      { heading: "Protege tu pago", paragraphs: ["Elige métodos seguros y trazables. Evita transferencias directas o pagos con tarjetas regalo a desconocidos."] },
      { heading: "Protege tus datos", paragraphs: ["Nunca compartas contraseñas, datos completos de tarjeta ni documentos fuera del pago oficial. Las tiendas legítimas no los piden por mensaje."] },
      { heading: "Conoce los riesgos de envío", paragraphs: ["Los pedidos internacionales pueden conllevar aduanas y plazos mayores. Lee las políticas de envío y devoluciones para saber qué esperar."] },
    ],
    tableTitle: "Buenas prácticas",
    table: {
      headers: ["Área", "Buena práctica"],
      rows: [
        ["Sitio", "Solo dominio oficial"],
        ["Pago", "Métodos seguros"],
        ["Datos", "Nunca fuera del pago"],
      ],
    },
    faq: [
      { q: "¿Es seguro comprar en USFans?", a: "Puede serlo cuando usas el sitio oficial y métodos seguros. Cuidado con enlaces no oficiales." },
      { q: "¿Qué métodos de pago son más seguros?", a: "Usa métodos seguros y trazables disponibles en el pago oficial." },
      { q: "¿Qué no debo compartir nunca?", a: "Nunca compartas contraseñas, datos completos de tarjeta ni documentos fuera del pago oficial." },
    ],
  },
  {
    slug: "usfans-qc",
    title: "Control de calidad de USFans: cómo comprobar antes de comprar",
    shortAnswer:
      "El control de calidad (QC) consiste en revisar fotos, tallas y descripciones del material antes de pedir. Buenos hábitos de QC ayudan a evitar artículos que se ven distintos en persona.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "6 min de lectura",
    heroImage: IMG_QC,
    toc: ["Qué significa QC", "Qué buscar", "Leer las fotos", "Cuándo preguntar"],
    sections: [
      { heading: "Qué significa QC", paragraphs: ["El control de calidad es comprobar que un producto cumple expectativas antes del envío. Para el comprador, es revisar las fichas con cuidado antes de pedir."] },
      { heading: "Qué buscar", paragraphs: ["Busca fotos claras y múltiples, tablas de tallas detalladas y descripciones del material. Cuanto más detalle, más fácil de evaluar."] },
      { heading: "Leer las fotos", paragraphs: ["Compara las fotos con la descripción. Si una ficha tiene una sola foto o una imagen muy editada, trátala con cautela."] },
      { heading: "Cuándo preguntar", paragraphs: ["Si la talla, el material o el color no está claro, busca más información o compara con artículos similares. Unos minutos de revisión evitan decepciones."] },
    ],
    tableTitle: "Lista de control QC",
    table: {
      headers: ["Elemento", "Qué comprobar"],
      rows: [
        ["Fotos", "Claras y múltiples"],
        ["Tallas", "Tabla detallada"],
        ["Material", "Descrito con claridad"],
      ],
    },
    faq: [
      { q: "¿Qué significa QC en USFans?", a: "QC es control de calidad, comprobar los productos frente a las expectativas antes del envío." },
      { q: "¿Cómo compruebo la calidad antes de comprar?", a: "Revisa fotos, tablas de tallas y descripciones del material, y compara con artículos similares." },
      { q: "¿Y si una ficha tiene pocas fotos?", a: "Trátala con cautela y busca más información antes de pedir." },
    ],
  },
  {
    slug: "usfans-sellers",
    title: "Vendedores de USFans: guía para encontrar fiables",
    shortAnswer:
      "Un vendedor fiable de USFans tiene fichas claras, opiniones coherentes y políticas transparentes. Revisar estas señales te ayuda a evitar vendedores poco fiables.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "6 min de lectura",
    heroImage: IMG_SELLERS,
    toc: ["Por qué importan los vendedores", "Señales de un buen vendedor", "Señales de alerta", "Comparar vendedores"],
    sections: [
      { heading: "Por qué importan los vendedores", paragraphs: ["Los vendedores determinan la calidad, la velocidad de envío y cómo se resuelven los problemas. Elegir un buen vendedor es tan importante como elegir el producto."] },
      { heading: "Señales de un buen vendedor", paragraphs: ["Busca fichas detalladas, opiniones positivas coherentes y políticas claras de envío y devolución. Los buenos vendedores comunican con claridad."] },
      { heading: "Señales de alerta", paragraphs: ["Desconfía de vendedores con fichas vagas, opiniones incoherentes o peticiones de pago inusuales. Son signos habituales de poca fiabilidad."] },
      { heading: "Comparar vendedores", paragraphs: ["Si varios vendedores ofrecen un artículo similar, compara fotos, descripciones y opiniones. Pequeñas diferencias revelan mucho sobre la fiabilidad."] },
    ],
    tableTitle: "Señales del vendedor",
    table: {
      headers: ["Señal", "Buena", "Alerta"],
      rows: [
        ["Fichas", "Detalladas", "Vagas"],
        ["Opiniones", "Coherentes", "Incoherentes"],
        ["Políticas", "Claras", "Poco claras"],
      ],
    },
    faq: [
      { q: "¿Cómo encuentro un buen vendedor de USFans?", a: "Busca fichas detalladas, opiniones coherentes y políticas claras antes de pedir." },
      { q: "¿Cuáles son las señales de alerta?", a: "Fichas vagas, opiniones incoherentes y peticiones de pago inusuales son signos habituales." },
      { q: "¿Puedo comparar vendedores?", a: "Sí. Compara fotos, descripciones y opiniones cuando varios vendedores ofrecen un artículo similar." },
    ],
  },
  {
    slug: "usfans-products",
    title: "Productos de USFans: qué puedes encontrar",
    shortAnswer:
      "Los productos de USFans abarcan ropa urbana y artículos para fans, como zapatillas, sudaderas, camisetas, camisetas deportivas, bolsos y accesorios. Conocer la gama te ayuda a encontrar más rápido lo que buscas.",
    author: "Equipo editorial de USFans",
    updated: "Actualizado en agosto de 2026",
    readTime: "6 min de lectura",
    heroImage: IMG_PRODUCTS,
    toc: ["La gama de productos", "Categorías populares", "Cómo se listan", "Elegir el artículo correcto"],
    sections: [
      { heading: "La gama de productos", paragraphs: ["USFans cubre una amplia gama de ropa urbana y artículos para fans, desde calzado y ropa hasta accesorios y bolsos. El catálogo los agrupa para navegar rápido."] },
      { heading: "Categorías populares", paragraphs: ["Zapatillas, sudaderas y camisetas suelen ser las secciones más populares. Accesorios, bolsos y camisetas deportivas son comunes para regalos o piezas concretas."] },
      { heading: "Cómo se listan", paragraphs: ["Los productos se agrupan por categoría, con enlaces al sitio principal para todos los detalles y precios. Esto hace que navegar sea predecible."] },
      { heading: "Elegir el artículo correcto", paragraphs: ["Decide qué necesitas y ve directo a esa categoría. Compara algunos artículos similares antes de elegir para asegurarte de que cumplen tus expectativas."] },
    ],
    tableTitle: "Categorías populares",
    table: {
      headers: ["Categoría", "Uso típico"],
      rows: [
        ["Zapatillas", "Uso diario"],
        ["Sudaderas y camisetas", "Ropa casual"],
        ["Accesorios y bolsos", "Regalos y extras"],
      ],
    },
    faq: [
      { q: "¿Qué productos vende USFans?", a: "El catálogo abarca calzado, ropa, camisetas deportivas, accesorios, bolsos y más, agrupados por categoría." },
      { q: "¿Qué categorías son más populares?", a: "Zapatillas, sudaderas y camisetas suelen ser las más populares." },
      { q: "¿Cómo se organizan los productos?", a: "Se agrupan por categoría, con enlaces al sitio principal para todos los detalles." },
    ],
  },
];